import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MapPin, CheckCircle2, Trash2, Lock, Filter, Users, User as UserIcon, ChevronDown, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useAuth } from "../context/AuthContext";
import { formatDisplay } from "../components/HVDateInput";

const STATUS_META = {
  draft:     { label: "Draft",     color: "#647087" },
  active:    { label: "Active",    color: "#1fb6c1" },
  completed: { label: "Completed", color: "#10B981" },
  cancelled: { label: "Cancelled", color: "#ef4444" },
};

const FILTERS = [
  { key: "all",       label: "All" },
  { key: "upcoming",  label: "Upcoming" },
  { key: "active",    label: "Active" },
  { key: "past",      label: "Past" },
];

const SCOPES = [
  { key: "mine", label: "Mine", icon: UserIcon },
  { key: "team", label: "Whole team", icon: Users },
  { key: "person", label: "Specific person", icon: UserIcon },
];

/* ─── Layer 2 — Trip Home (Wave 13 — 2026-02 #71) ─────────────────────
   Now visible to every staff role with three scope tabs:
     • Mine    — only the current user's trips
     • Whole team — every trip in the system, grouped by owner
     • Specific person — pick one rep, see only their trips
   Each scope still respects the lifecycle filter (All / Upcoming / Active / Past). */
export default function Trips() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [scope, setScope] = useState("mine");
  const [personId, setPersonId] = useState("");
  const [collapsed, setCollapsed] = useState({}); // { [ownerId]: true|false }

  const load = async (opts) => {
    const silent = !!opts?.silent;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/trips");
      setTrips(data || []);
    } catch (_e) { /* noop */ }
    if (!silent) setLoading(false);
  };
  useEffect(() => { load(); }, []);
  // Auto-refresh while the tab is foreground (Wave 19 — concurrent-edit safety).
  useAutoRefresh(load, { intervalMs: 30_000 });

  const createBlank = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await api.post("/trips", {
      title: "New trip",
      start_date: today,
      stops: [],
    });
    navigate(`/trips/${data.id}`);
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete trip "${title}"? Associated calendar events + tasks will be removed.`)) return;
    await api.delete(`/trips/${id}`);
    load();
  };

  // Apply scope first
  const scopedTrips = useMemo(() => {
    if (scope === "mine") return trips.filter((t) => t.owner_user_id === user?.id);
    if (scope === "person" && personId) return trips.filter((t) => t.owner_user_id === personId);
    return trips; // team
  }, [trips, scope, personId, user?.id]);

  const buckets = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = [], active = [], past = [];
    for (const t of scopedTrips) {
      if (t.status === "active") active.push(t);
      else if (t.status === "completed" || t.status === "cancelled") past.push(t);
      else if ((t.start_date || "") >= today) upcoming.push(t);
      else past.push(t);
    }
    upcoming.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
    active.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
    past.sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
    return { upcoming, active, past };
  }, [scopedTrips]);

  const visible = useMemo(() => {
    if (filter === "upcoming") return buckets.upcoming;
    if (filter === "active") return buckets.active;
    if (filter === "past") return buckets.past;
    return [...buckets.active, ...buckets.upcoming, ...buckets.past];
  }, [filter, buckets]);

  // For "Whole team" — group by owner. Current user appears first, expanded.
  // Other staff sections start collapsed (toggleable per row).
  const grouped = useMemo(() => {
    if (scope !== "team") return null;
    const byOwner = {};
    for (const t of visible) {
      const k = t.owner_user_id || "_unknown";
      (byOwner[k] = byOwner[k] || []).push(t);
    }
    const ids = Object.keys(byOwner);
    ids.sort((a, b) => {
      // current user always first
      if (a === user?.id) return -1;
      if (b === user?.id) return 1;
      const an = (byOwner[a][0]?.owner_name || "").toLowerCase();
      const bn = (byOwner[b][0]?.owner_name || "").toLowerCase();
      return an.localeCompare(bn);
    });
    return ids.map((ownerId) => ({
      ownerId,
      ownerName: byOwner[ownerId][0]?.owner_name || (ownerId === user?.id ? "Me" : "Unknown"),
      ownerRole: byOwner[ownerId][0]?.owner_role,
      trips: byOwner[ownerId],
    }));
  }, [visible, scope, user?.id]);

  // Pull the staff list for the "Specific person" tab. Only call when needed.
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    if (scope !== "person" || staff.length) return;
    api.get("/users")
      .then((r) => setStaff((r.data || []).filter((u) => u.role && u.role !== "client" && u.active !== false)))
      .catch(() => {});
  }, [scope, staff.length]);

  return (
    <div className="space-y-5" data-testid="trips-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Trip planner</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <MapPin className="w-7 h-7 text-[#1fb6c1]" />
            Trips
          </h1>
          <p className="text-sm text-[#a0aabf] mt-2 max-w-2xl">
            Plan, run, and review every customer-visit trip. Click any trip for the immersive command centre — map, calendar, sales briefs, contacts, AI insights, and the printable itinerary.
          </p>
        </div>
        <button
          type="button"
          onClick={createBlank}
          className="hv-btn hv-btn-primary"
          data-testid="trip-new-btn"
        >
          <Plus className="w-4 h-4" /> New trip
        </button>
      </div>

      {/* Scope tabs */}
      <div className="flex items-center gap-2 overflow-x-auto" data-testid="trips-scope-tabs">
        {SCOPES.map((s) => {
          const Icon = s.icon;
          const active = scope === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setScope(s.key)}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border whitespace-nowrap transition inline-flex items-center gap-1.5 ${active ? "bg-[#1fb6c1] text-[#0a0b0e] border-[#1fb6c1]" : "border-white/10 text-[#a0aabf] hover:border-[#1fb6c1]/40"}`}
              data-testid={`trips-scope-${s.key}`}
            >
              <Icon className="w-3 h-3" /> {s.label}
            </button>
          );
        })}
        {scope === "person" && (
          <select
            value={personId}
            onChange={(e) => setPersonId(e.target.value)}
            className="hv-input py-1 px-2 text-xs ml-2"
            data-testid="trips-person-select"
          >
            <option value="">— pick someone —</option>
            {staff.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim()}
                {u.role ? ` · ${u.role}` : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 overflow-x-auto" data-testid="trips-filter">
        <Filter className="w-3.5 h-3.5 text-[#647087] shrink-0" />
        {FILTERS.map((f) => {
          const count = f.key === "all" ? scopedTrips.length
            : f.key === "upcoming" ? buckets.upcoming.length
            : f.key === "active" ? buckets.active.length
            : buckets.past.length;
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider border whitespace-nowrap transition ${active ? "bg-[#1fb6c1] text-[#0a0b0e] border-[#1fb6c1]" : "border-white/10 text-[#a0aabf] hover:border-[#1fb6c1]/40"}`}
              data-testid={`trips-filter-${f.key}`}
            >
              {f.label} <span className="opacity-60">{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-xs text-[#647087] py-10 text-center">Loading trips…</div>
      ) : scope === "person" && !personId ? (
        <div className="hv-card p-8 text-center text-xs text-[#647087]">Pick a person above to see their trips.</div>
      ) : visible.length === 0 ? (
        <div className="hv-card p-8 text-center" data-testid="trips-empty">
          <MapPin className="w-10 h-10 text-[#1fb6c1] mx-auto mb-2" />
          <h3 className="font-display text-lg font-bold">No trips here</h3>
          <p className="text-xs text-[#a0aabf] mt-1 mb-4">
            {filter === "all"
              ? "Plan your first customer visit. Pick prospects, set times, hit Save."
              : `No ${filter} trips. Try a different filter or create a new trip.`}
          </p>
          {filter === "all" && scope === "mine" && (
            <button type="button" onClick={createBlank} className="hv-btn hv-btn-primary">
              <Plus className="w-4 h-4" /> Create first trip
            </button>
          )}
        </div>
      ) : grouped ? (
        // Whole-team view: per-staff collapsible sections
        <div className="space-y-4">
          {grouped.map(({ ownerId, ownerName, ownerRole, trips: list }) => {
            const isMe = ownerId === user?.id;
            const open = isMe ? !collapsed[ownerId] : !!collapsed[ownerId]; // me=open by default
            return (
              <div key={ownerId} className="hv-card p-3" data-testid={`trips-team-section-${ownerId}`}>
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [ownerId]: isMe ? !c[ownerId] : !c[ownerId] }))}
                  className="w-full flex items-center justify-between gap-3"
                  data-testid={`trips-team-toggle-${ownerId}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {open ? <ChevronDown className="w-4 h-4 text-[#1fb6c1]" /> : <ChevronRight className="w-4 h-4 text-[#647087]" />}
                    <span className="font-display text-base font-bold truncate">
                      {isMe ? "My trips" : ownerName}
                    </span>
                    {ownerRole && <span className="text-[10px] uppercase tracking-wider text-[#A78BFA]">{ownerRole}</span>}
                  </div>
                  <span className="text-[10px] text-[#647087]">{list.length} trip{list.length === 1 ? "" : "s"}</span>
                </button>
                {open && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                    {list.map((t) => <TripCard key={t.id} trip={t} onDelete={del} canDelete={t.owner_user_id === user?.id || ["owner", "admin", "manager"].includes(user?.role)} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((t) => (
            <TripCard
              key={t.id}
              trip={t}
              onDelete={del}
              canDelete={t.owner_user_id === user?.id || ["owner", "admin", "manager"].includes(user?.role)}
              showOwner={scope === "person"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TripCard({ trip: t, onDelete, canDelete = true, showOwner = false }) {
  const meta = STATUS_META[t.status] || STATUS_META.draft;
  const nextStop = (t.stops || []).find((s) => s.status === "pending") || (t.stops || [])[0];
  const visited = (t.stops || []).filter((s) => s.status === "visited").length;
  const total = (t.stops || []).length;
  const dateRange = `${formatDisplay(t.start_date)}${t.end_date && t.end_date !== t.start_date ? ` → ${formatDisplay(t.end_date)}` : ""}`;
  return (
    <Link
      to={`/trips/${t.id}`}
      data-testid={`trip-card-${t.id}`}
      className="hv-card p-4 hover:border-[#1fb6c1]/40 transition flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-bold truncate">{t.title}</div>
          <div className="text-[10px] font-mono text-[#647087] mt-0.5">
            {dateRange}{t.start_time ? ` · ${t.start_time}` : ""}
          </div>
          {showOwner && t.owner_name && (
            <div className="text-[10px] text-[#A78BFA] mt-0.5">{t.owner_name}</div>
          )}
        </div>
        <span
          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 flex items-center gap-1"
          style={{ borderColor: `${meta.color}55`, background: `${meta.color}15`, color: meta.color }}
          data-testid={`trip-card-${t.id}-status`}
        >
          {t.status === "active" && <Lock className="w-2.5 h-2.5" />}
          {t.status === "completed" && <CheckCircle2 className="w-2.5 h-2.5" />}
          {meta.label}
        </span>
      </div>
      <div className="text-xs text-[#a0aabf] flex items-center gap-2">
        <MapPin className="w-3 h-3 text-[#1fb6c1]" />
        <span>{total} stop{total === 1 ? "" : "s"}</span>
        {total > 0 && <span className="text-[#647087]">· {visited}/{total} visited</span>}
        {nextStop && <span className="text-[#647087] truncate">· next: {nextStop.title}</span>}
      </div>
      <div className="flex items-center gap-2 pt-2 border-t border-white/5" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <span className="text-[10px] text-[#647087]">
          {t.start_address ? `from ${t.start_address.slice(0, 32)}${t.start_address.length > 32 ? "…" : ""}` : "no start point"}
        </span>
        {canDelete && (
          <button
            type="button"
            onClick={() => onDelete(t.id, t.title)}
            className="ml-auto hv-btn hv-btn-ghost text-[10px] py-1"
            data-testid={`trip-delete-${t.id}`}
          >
            <Trash2 className="w-3 h-3 text-rose-300" />
          </button>
        )}
      </div>
    </Link>
  );
}
